"use client";

import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";

const BILLGUARD_LOGO_URL = "https://ui.prt.news/hg6lujo2xh001f5g.png";

const BillGuardClientContent = ({ children }: { children: ReactNode }) => {
  return (
    <div className="h-screen overflow-auto !bg-white">
      <div className="sticky top-0 z-10">
        <Header />
      </div>
      <div className="h-auto relative">
        {children}
        <div className="mt-auto">
          <Footer />
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const DEFAULT_ICON_URL = "/images/favicon.png";
    const selectors = ["link[rel='icon']", "link[rel='apple-touch-icon']"];
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>(selectors.join(","))
    );
    const previous = links.map((link) => ({
      link,
      href: link.getAttribute("href"),
    }));

    links.forEach((link) => {
      link.setAttribute("href", BILLGUARD_LOGO_URL);
    });

    return () => {
      previous.forEach(({ link, href }) => {
        link.setAttribute("href", href || DEFAULT_ICON_URL);
      });
    };
  }, []);

  useEffect(() => {
    const hamburger = document.querySelector(".hamburger-menu");
    const navLinks = document.querySelector(".nav-links");
    const bars = Array.from(document.querySelectorAll(".hamburger-menu .bar"));

    if (!hamburger || !navLinks) return;

    const setBarsChanged = (changed: boolean) => {
      bars.forEach((bar) => {
        if (changed) bar.classList.add("change");
        else bar.classList.remove("change");
      });
    };

    const toggleMenu = () => {
      navLinks.classList.toggle("active");
      setBarsChanged(navLinks.classList.contains("active"));
    };

    const closeMenu = () => {
      navLinks.classList.remove("active");
      setBarsChanged(false);
    };

    hamburger.addEventListener("click", toggleMenu);

    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(".nav-links a"));
    links.forEach((link) => link.addEventListener("click", closeMenu));

    return () => {
      hamburger.removeEventListener("click", toggleMenu);
      links.forEach((link) => link.removeEventListener("click", closeMenu));
    };
  }, []);

  return (
    <nav className="nav-menu w-full shadow-sm sticky top-0 z-10 !bg-white">
      <div className="logo-container flex items-center gap-2">
        <span className="text-teal-700 font-bold text-lg">BillGuard</span>
        <a href="/billguard/terms" className="title text-teal-700 tracking-tight" aria-label="BillGuard">
          {!logoError ? (
            <img
              src={BILLGUARD_LOGO_URL}
              alt="BillGuard"
              style={{ maxHeight: "48px", maxWidth: "48px", height: "48px", width: "48px" }}
              className="rounded"
              onError={() => setLogoError(true)}
            />
          ) : (
            <span className="font-semibold">BillGuard</span>
          )}
        </a>
      </div>
      <div className="hamburger-menu">
        <span className="bar"></span>
        <span className="bar"></span>
        <span className="bar"></span>
      </div>
      <div className="nav-links">
        <a href="/billguard/terms">Terms of Service</a>
        <a href="/billguard/privacy">Privacy Policy</a>
        <a href="/billguard/dsmi">Your Privacy Choices</a>
        <a href="/billguard/legal-notice">Legal Notice</a>
        <a href="/billguard/contact">Contact Us</a>
      </div>
    </nav>
  );
};

const Footer = () => {
  return (
    <footer className="bg-white border-t border-gray-200 mt-12">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[#293c58] mb-4">BillGuard</h3>
            <p className="text-gray-600 text-sm mb-4">
              AI-powered medical bill analysis by Life Agents, LLC.
            </p>
            <div className="text-gray-600 text-sm">
              <p className="mb-1">
                <strong>Email:</strong>{" "}
                <a href="mailto:info@lifeagents.ai" className="text-teal-700 hover:text-teal-900 underline">
                  info@lifeagents.ai
                </a>
              </p>
              <p>
                <strong>Address:</strong>
                <br />
                <span className="text-gray-500">
                  800 W El Camino Real Ste 310
                  <br />
                  Mountain View, CA 94040-2589
                </span>
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-[#293c58] mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <a href="/billguard/terms" className="text-gray-600 hover:text-teal-700 text-sm transition-colors">
                  Terms of Service
                </a>
              </li>
              <li>
                <a href="/billguard/privacy" className="text-gray-600 hover:text-teal-700 text-sm transition-colors">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/billguard/dsmi" className="text-gray-600 hover:text-teal-700 text-sm transition-colors">
                  Your Privacy Choices
                </a>
              </li>
              <li>
                <a href="/billguard/legal-notice" className="text-gray-600 hover:text-teal-700 text-sm transition-colors">
                  Legal Notice
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-[#293c58] mb-4">Support</h3>
            <ul className="space-y-2">
              <li>
                <a href="/billguard/contact" className="text-gray-600 hover:text-teal-700 text-sm transition-colors">
                  Contact Us
                </a>
              </li>
              <li>
                <a href="mailto:info@lifeagents.ai" className="text-gray-600 hover:text-teal-700 text-sm transition-colors">
                  Privacy Inquiries
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-gray-500 text-xs">
            <p>Copyright © 2026 Life Agents, LLC. All rights reserved.</p>
            <p className="mt-2 md:mt-0">BillGuard is a service by Life Agents, LLC</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default BillGuardClientContent;
